/**
 * @file     PremiumPanelGate.tsx
 * @purpose  Inline soft paywall around a property-detail intelligence
 *           panel. Renders children fully when the visitor is
 *           authenticated OR hasn't crossed the viewed-listings threshold.
 *           After threshold, renders the children behind the same single-layer
 *           blur used by VOW values
 *           with a centered CTA that opens the AuthGateModal in-place
 *           (no redirect, they stay on the listing).
 *
 *           Fires Relta auth_gate.panel_locked.shown on first scroll-into-
 *           view (per panel) and auth_gate.panel_locked.unlock_clicked on
 *           CTA click. A sessionStorage breadcrumb is set so the
 *           subsequent auth.signin can correlate as auth_gate.converted.
 */
'use client';

import { useEffect, useRef, useState } from 'react';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import { useAppTheme } from '@/core/theme/useAppTheme';
import { useAuth } from '@/hooks/useAuth';
import { useViewedListings } from '@/hooks/useViewedListings';
import { isGateEligible } from '@/hooks/useListingsAccess';
import { useListingEntrySource } from '@/hooks/useListingEntrySource';
import { reltaTrack } from '@/hooks/useReltaTrack';
import { AuthGateModal } from './AuthGateModal';
import { useAnalytics } from '@/hooks/useAnalytics';
import { analyticsEvents } from '@/core/constants/analytics';
import { canApplyListingGate } from '@/services/auth/listingEntrySource';

export const GATE_CONVERSION_KEY = 'relta.auth_gate.unlock_intent';

interface Props {
  /** Stable identifier, same constants used in the Relta event taxonomy. */
  panel: 'my_read' | 'market_story' | 'supply_outlook' | 'area_mix' | 'demographics';
  listingKey?: string;
  /** Short blurb shown above the CTA explaining what's behind the lock. */
  teaserTitle: string;
  teaserBody: string;
  /** Blur the full panel, or only descendants marked data-premium-value. */
  treatment?: 'panel' | 'values';
  analyticsSurface?: 'slug_page' | 'map_sheet';
  children: React.ReactNode;
}

export function PremiumPanelGate({
  panel,
  listingKey,
  teaserTitle,
  teaserBody,
  treatment = 'panel',
  analyticsSurface,
  children,
}: Props) {
  const { theme } = useAppTheme();
  const sc = theme.semanticColors;
  const { isAuthenticated, isLoading } = useAuth();
  const { trackEvent } = useAnalytics();
  const { hasReachedThreshold } = useViewedListings();
  const { isInternalEntry } = useListingEntrySource();
  const ref = useRef<HTMLDivElement | null>(null);
  const impressionFired = useRef(false);
  const [modalOpen, setModalOpen] = useState(false);

  // Shared floor: no sign-up wall before the full allowance of properties has
  // been seen. Read after mount so the panel renders open on
  // the server and first paint.
  const [pastFloor, setPastFloor] = useState(false);
  useEffect(() => {
    setPastFloor(isGateEligible());
  }, [listingKey]);

  const standaloneEntryCanGate = canApplyListingGate(analyticsSurface, isInternalEntry);
  const locked =
    !isLoading && !isAuthenticated && pastFloor && hasReachedThreshold && standaloneEntryCanGate;

  useEffect(() => {
    if (!locked) return;
    if (typeof IntersectionObserver === 'undefined') return;
    const el = ref.current;
    if (!el || impressionFired.current) return;
    const obs = new IntersectionObserver(
      (entries) => {
        for (const e of entries) {
          if (e.isIntersecting && !impressionFired.current) {
            impressionFired.current = true;
            reltaTrack('auth_gate.panel_locked.shown', {
              panel,
              ...(listingKey ? { listing_key: listingKey } : {}),
            });
            trackEvent({
              eventName: analyticsEvents.propertyGateViewed,
              eventType: 'section',
              category: 'property_detail',
              label: panel,
              metadata: {
                panel,
                listing_key: listingKey,
                surface: analyticsSurface ?? 'unknown',
              },
            });
            obs.disconnect();
            return;
          }
        }
      },
      { threshold: 0.3 },
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, [locked, panel, listingKey]);

  if (!locked) return <>{children}</>;

  const onUnlock = () => {
    reltaTrack('auth_gate.panel_locked.unlock_clicked', {
      panel,
      ...(listingKey ? { listing_key: listingKey } : {}),
    });
    trackEvent({
      eventName: analyticsEvents.propertyGateUnlockClicked,
      eventType: 'click',
      category: 'property_detail',
      label: panel,
      metadata: {
        panel,
        listing_key: listingKey,
        surface: analyticsSurface ?? 'unknown',
      },
    });
    // Drop a breadcrumb so AuthProvider's SIGNED_IN handler can fire
    // auth_gate.converted on the next session. Cleared at consume time.
    try {
      sessionStorage.setItem(
        GATE_CONVERSION_KEY,
        JSON.stringify({ panel, listing_key: listingKey ?? null, ts: Date.now() }),
      );
    } catch { /* fire-and-forget */ }
    setModalOpen(true);
  };

  if (treatment === 'values') {
    return (
      <Box
        ref={ref}
        onClickCapture={(event) => {
          const target = event.target as HTMLElement;
          if (!target.closest('[data-premium-value="true"]')) return;
          event.preventDefault();
          event.stopPropagation();
          onUnlock();
        }}
        sx={{
          '& [data-premium-value="true"]': {
            filter: 'blur(5px)',
            transition: 'filter .35s',
            userSelect: 'none',
            cursor: 'pointer',
          },
        }}
      >
        <Box
          sx={{
            mb: 2,
            display: 'flex',
            alignItems: { xs: 'flex-start', sm: 'center' },
            justifyContent: 'space-between',
            flexDirection: { xs: 'column', sm: 'row' },
            gap: 2,
          }}
        >
          <Box>
            <Typography variant="subtitle2" sx={{ color: sc.textPrimary, fontWeight: 600 }}>
              {teaserTitle}
            </Typography>
            <Typography variant="caption" sx={{ color: sc.textSecondary }}>
              {teaserBody}
            </Typography>
          </Box>
          <Button
            variant="contained"
            onClick={onUnlock}
            sx={{
              flexShrink: 0,
              backgroundColor: sc.interactivePrimary,
              color: sc.textOnPrimary,
              textTransform: 'none',
              fontWeight: 500,
              borderRadius: 1,
              px: 5,
              '&:hover': { backgroundColor: sc.interactiveHover },
            }}
          >
            Sign up free
          </Button>
        </Box>
        {children}
        <AuthGateModal open={modalOpen} onDismiss={() => setModalOpen(false)} />
      </Box>
    );
  }

  return (
    <Box
      ref={ref}
      sx={{
        position: 'relative',
        mt: '28px',
        p: '24px 28px',
        borderRadius: '22px',
        backgroundColor: sc.surfaceCard,
        border: `1px solid ${sc.borderSubtle}`,
        boxShadow: '0 1px 2px rgba(0,0,0,0.04)',
        overflow: 'hidden',
        minHeight: 360,
      }}
    >
      {/* Match the VOW value treatment: one 5px content blur with the same
          reveal transition. Pointer events stay disabled while locked. */}
      <Box
        aria-hidden
        sx={{
          filter: 'blur(5px)',
          transition: 'filter .35s',
          pointerEvents: 'none',
          userSelect: 'none',
          minHeight: 320,
        }}
      >
        {children}
      </Box>

      {/* Keep the CTA crisp. A second backdrop blur made these panels look
          different from VOW-gated data even when the content blur matched. */}
      <Box
        sx={{
          position: 'absolute',
          inset: 0,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 1,
          p: 3,
          textAlign: 'center',
          // No background fade, let the blur breathe through.
        }}
      >
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: 1,
            p: 3,
            borderRadius: 1,
            backgroundColor: sc.surfaceCard,
            border: `1px solid ${sc.borderSubtle}`,
            boxShadow: '0 8px 32px rgba(0,0,0,0.18)',
            maxWidth: 420,
          }}
        >
          <Box
            aria-hidden
            sx={{
              display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
              width: 32, height: 32, borderRadius: '50%',
              backgroundColor: sc.surfaceElevated ?? sc.backgroundPrimary,
              border: `1px solid ${sc.borderSubtle}`,
              color: sc.textSecondary, fontSize: 14, lineHeight: 1,
            }}
          >
            ◆
          </Box>
          <Typography variant="subtitle1" sx={{ color: sc.textPrimary, fontWeight: 500 }}>
            {teaserTitle}
          </Typography>
          <Typography variant="body2" sx={{ color: sc.textSecondary }}>
            {teaserBody}
          </Typography>
          <Button
            variant="contained"
            onClick={onUnlock}
            sx={{
              mt: 0.5,
              backgroundColor: sc.interactivePrimary,
              color: sc.textOnPrimary,
              textTransform: 'none',
              fontWeight: 500,
              borderRadius: 1,
              px: 5,
              py: 1,
              '&:hover': { backgroundColor: sc.interactiveHover },
            }}
          >
            Sign up free
          </Button>
          <Typography variant="caption" sx={{ color: sc.textMuted }}>
            No credit card.
          </Typography>
        </Box>
      </Box>

      <AuthGateModal
        open={modalOpen}
        onDismiss={() => setModalOpen(false)}
      />
    </Box>
  );
}
